import { bucketName_files, tableName, prompts } from "../components/env";
import { generateMedADocsPath } from "../documentComponents/documentUtilities";
import { deleteFilesWithCacheCleanup } from "./storageUtils";

// Deleting a visit row (notes-c) from anywhere but the schedules table.
//
// Mirrors SchedulesTable.handleRemovePatient step for step: the row's chart
// document file goes from storage (with its cache entry), its `documents`
// table row goes, then the row itself. Rows may come from several schemas
// (the /search page tags each with `_schema`), so the caller supplies the
// client for a schema and the work is grouped per schema.
//
// Document cleanup is best-effort and never blocks the row delete — a chart
// that was never written has no file, and that is the common case.

// PostgREST puts .in() lists in the URL, so a large selection would blow the
// length limit. Every id-keyed request is chunked.
export const chunkIds = (ids, size = 50) => {
  const out = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
};

const NAME_COLUMN = prompts[0][0];

// Same filename SchedulesTable derives: `${name}-${EHR_ID || id}.json` under
// the row's year/date folder, schema-prefixed for tenants.
export const visitDocumentPath = async (row, schema) => {
  const date = row.date_ || new Date().toISOString().slice(0, 10);
  const year = date.split("-")[0];
  const patientName = row[NAME_COLUMN] || "SOAP_note";
  const idForFile = row.EHR_ID || row.id;
  return generateMedADocsPath(year, date, `${patientName}-${idForFile}.json`, schema || "public");
};

export const groupRowsBySchema = (rows, homeSchema) => {
  const groups = new Map();
  rows.forEach((row) => {
    const schema = row._schema || homeSchema || "public";
    if (!groups.has(schema)) groups.set(schema, []);
    groups.get(schema).push(row);
  });
  return groups;
};

/**
 * @param {object} p
 * @param {object[]} p.rows          notes-c rows to delete (id, date_, name, EHR_ID, _schema)
 * @param {(schema: string) => object} p.clientFor  PostgREST client scoped to that schema
 * @param {string} p.homeSchema      schema for rows that carry no `_schema`
 * @returns {Promise<{deletedIds: string[], failed: {id: string, error: string}[]}>}
 */
export async function removeVisitRows({ rows, clientFor, homeSchema }) {
  const deletedIds = [];
  const failed = [];
  for (const [schema, group] of groupRowsBySchema(rows, homeSchema)) {
    const client = clientFor(schema);

    const paths = [];
    for (const row of group) {
      try {
        // eslint-disable-next-line no-await-in-loop
        paths.push(await visitDocumentPath(row, schema));
      } catch (e) {
        console.warn(`[removeVisitRows] could not build document path for ${row.id}:`, e?.message);
      }
    }
    if (paths.length > 0) {
      try {
        await deleteFilesWithCacheCleanup(client, bucketName_files, paths);
      } catch (e) {
        console.warn("[removeVisitRows] document file cleanup failed:", e?.message);
      }
      for (const pathChunk of chunkIds(paths)) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const { error } = await client.from("documents").delete().in("path", pathChunk);
          if (error) console.warn("[removeVisitRows] documents table cleanup failed:", error.message);
        } catch (e) {
          console.warn("[removeVisitRows] documents table cleanup threw:", e?.message);
        }
      }
    }

    const ids = group.map((row) => String(row.id));
    for (const chunk of chunkIds(ids)) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const { error } = await client.from(tableName).delete().in("id", chunk);
        if (error) {
          chunk.forEach((id) => failed.push({ id, error: error.message }));
        } else {
          deletedIds.push(...chunk);
        }
      } catch (e) {
        chunk.forEach((id) => failed.push({ id, error: e?.message || String(e) }));
      }
    }
  }
  return { deletedIds, failed };
}
